import { SignUpPolicyLabel } from "components/shared";
import {
  Button,
  Checkbox,
  Dialog,
  FormContainer,
  FormItem,
  Input,
  Notification,
  toast,
} from "components/ui";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiUpdateEmail } from "services/AuthService";
import * as Yup from "yup";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {object} [authResponse] - OTP/CodeCheckBySms axios yanıtı (data içinde token, email)
 * @param {string} [persistedToken] - Zaten giriş yapmış kullanıcı (Redux session token)
 * @param {boolean} [hideCancel] - true ise iptal gizlenir (oturum zorunlu güncelleme)
 */
const UpdateEmailDialog = ({
  isOpen,
  onClose,
  authResponse,
  persistedToken,
  hideCancel = false,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t("auth.updateEmail.form.email.error"))
      .matches(EMAIL_REGEX, t("auth.updateEmail.form.email.invalidFormat")),
    policy: Yup.boolean()
      .required(t("auth.signUp.form.policy.error"))
      .oneOf([true], t("auth.signUp.form.policy.error")),
  });

  const token = authResponse?.data?.token ?? persistedToken;

  const handleSubmit = async (values) => {
    if (!token) return;

    setIsSubmitting(true);
    try {
      await apiUpdateEmail({
        email: values.email,
        token,
      });
      if (authResponse) {
        const updatedResponse = {
          ...authResponse,
          data: {
            ...(authResponse.data ?? {}),
            email: values.email,
          },
        };
        onSuccess?.(updatedResponse);
      } else {
        onSuccess?.({
          data: {
            email: values.email,
            token,
          },
        });
      }
      onClose?.();
    } catch (error) {
      toast.push(
        <Notification
          title={t("auth.updateEmail.error")}
          type="danger"
          duration={3500}
        >
          {error?.response?.data?.message ||
            error?.message ||
            t("auth.updateEmail.error")}
        </Notification>,
        { placement: "top-center" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      closable={false}
      isOpen={isOpen}
      onClose={() => onClose?.()}
      onRequestClose={() => onClose?.()}
      width={420}
    >
      <div>
        <h5 className="mb-4">{t("auth.updateEmail.title")}</h5>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {t("auth.updateEmail.description")}
        </p>
        <Formik
          initialValues={{
            email: "",
            policy: false,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ touched, errors }) => (
            <Form>
              <FormContainer>
                <FormItem
                  asterisk
                  label={t("auth.updateEmail.form.email.label")}
                  invalid={errors.email && touched.email}
                  errorMessage={errors.email}
                >
                  <Field
                    type="email"
                    autoComplete="email"
                    name="email"
                    placeholder={t("auth.updateEmail.form.email.placeholder")}
                    component={Input}
                  />
                </FormItem>
                <FormItem
                  className="text-left"
                  invalid={errors.policy && touched.policy}
                  errorMessage={errors.policy}
                >
                  <Field
                    name="policy"
                    component={Checkbox}
                    children={<SignUpPolicyLabel />}
                  />
                </FormItem>
                <div className="flex gap-2 justify-end mt-4">
                  {!hideCancel && (
                    <Button
                      type="button"
                      variant="plain"
                      onClick={() => onClose?.()}
                      disabled={isSubmitting}
                    >
                      {t("auth.updateEmail.cancel")}
                    </Button>
                  )}
                  <Button type="submit" variant="solid" loading={isSubmitting}>
                    {t("auth.updateEmail.submit")}
                  </Button>
                </div>
              </FormContainer>
            </Form>
          )}
        </Formik>
      </div>
    </Dialog>
  );
};

export default UpdateEmailDialog;
